import { IconOtherSound } from '@doltech/icons/IconOtherSound';
import { RequiredAuthFake } from '@doltech/ui/lib/figma/Auth/RequiredAuthFake';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import Image from 'next/image';
import { lowerCase } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { imgProxyLoader } from '@doltech/utils/lib/image-loader';
import { useSimpleAudioPlayInBackgroundStore } from '@doltech/ui/lib/hooks/simple-audio-play-in-background.store';

const WORD_CONTAINER_WIDTH = 240;

const VocabItemContainer = styled.div`
  display: grid;
  grid-template-columns: ${WORD_CONTAINER_WIDTH}px 1px minmax(0, 1fr);
  column-gap: 16px;
  background: ${colorsV2.gray5};
  border: 1px solid ${colorsV2.black10};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;

  .ic-sound {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
  }
  .word-container {
    width: ${WORD_CONTAINER_WIDTH}px;
  }
  .phonetics-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 8px;

    .ic-sound {
      margin-right: 12px;
    }
  }

  .word-meaning-text {
    display: grid;
    grid-template-columns: 1fr max-content;
    column-gap: 16px;
  }
  .word-example-image-view {
    height: 60px;
    width: 90px;

    position: relative;
  }
  .word-example-text {
    margin-top: 12px;
    .ic-sound {
      display: inline-flex;
      margin-left: 8px;
      vertical-align: middle;
      color: ${colorsV2.black100};
    }
  }

  @media ${deviceBreakpoints.mobile} {
    grid-template-columns: unset;
    .phonetics-container {
      padding: 0 0 12px;
    }
    .word-meaning-text {
      grid-template-columns: unset;
      grid-template-rows: auto auto;
      row-gap: 12px;
    }
  }
`;

interface VocabExpandCollapseProps {
  example?: string;
  image?: string;
  meaning?: string;
  pronounce?: string;
  pronounceFile?: {
    fileType?: 'PUBLIC' | 'PRIVATE';
    name?: string;
    path?: string;
    size?: number;
    uploadedDate?: string;
    url?: string;
  };
  type?:
    | 'NOUN'
    | 'VERB'
    | 'ADV'
    | 'ADJ'
    | 'PREPOSITION'
    | 'CONJUNCTION'
    | 'ARTICLE'
    | 'INTERJECTION'
    | 'PRONOUN'
    | 'PHRASE';
  value?: string;
}

export const VocabExampleComponent = observer((props: VocabExpandCollapseProps) => {
  const { value, type, pronounce, example, meaning, image } = props;
  const pronounText = pronounce?.startsWith('/') ? pronounce : `/${pronounce}/`;
  const audioStore = useSimpleAudioPlayInBackgroundStore();
  const onListenPhonetics = React.useCallback(() => {
    audioStore.playByPhonetics(pronounce);
  }, [audioStore, pronounce]);

  const renderVocabContent = React.useCallback(() => {
    const onListenExample = () => {
      audioStore.playByText(example);
    };
    if (image) {
      return (
        <div className="word-meaning-text">
          <div>
            <Typography.Text variant="regular/16">
              ({lowerCase(type)}). {meaning}
            </Typography.Text>
            <RequiredAuthFake
              title="Để truy cập full tính năng, bạn cần đăng ký tài khoản dolacademy.vn"
              onLoginSuccess={onListenExample}
            >
              {({ forceLoginToProceedAction }) => {
                return (
                  <div
                    aria-hidden="true"
                    role="button"
                    onClick={() => forceLoginToProceedAction(onListenExample)}
                    className="word-example-text"
                  >
                    <Typography.Text variant="regular/14" style={{ color: colorsV2.black80 }}>
                      {example}
                      <span className="ic-sound">
                        <IconOtherSound />
                      </span>
                    </Typography.Text>
                  </div>
                );
              }}
            </RequiredAuthFake>
          </div>
          {image && (
            <div className="word-example-image-view">
              <Image
                src={image}
                width="100%"
                height="100%"
                layout="fill"
                objectFit="cover"
                alt="vocab"
                loader={imgProxyLoader}
                priority={false}
              />
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        <Typography.Text variant="regular/16">
          ({lowerCase(type)}). {meaning}
        </Typography.Text>
        <RequiredAuthFake
          title="Để truy cập full tính năng, bạn cần đăng ký tài khoản dolacademy.vn"
          onLoginSuccess={onListenExample}
        >
          {({ forceLoginToProceedAction }) => {
            return (
              <div
                aria-hidden="true"
                role="button"
                onClick={() => forceLoginToProceedAction(onListenExample)}
                className="word-example-text"
              >
                <Typography.Text variant="regular/14" style={{ color: colorsV2.black80 }}>
                  {example}
                  <span className="ic-sound">
                    <IconOtherSound />
                  </span>
                </Typography.Text>
              </div>
            );
          }}
        </RequiredAuthFake>
      </div>
    );
  }, [audioStore, example, image, meaning, type]);

  return (
    <VocabItemContainer>
      <div className="word-container">
        <div className="vocab-phonetics">
          <Typography.Text variant="semi-bold/16">{value}</Typography.Text>{' '}
        </div>
        <div className="phonetics-container">
          <RequiredAuthFake
            title="Để truy cập full tính năng, bạn cần đăng ký tài khoản dolacademy.vn"
            onLoginSuccess={onListenPhonetics}
          >
            {({ forceLoginToProceedAction }) => {
              return (
                <i
                  className="ic-sound"
                  onClick={() => forceLoginToProceedAction(onListenPhonetics)}
                  role="button"
                  aria-hidden="true"
                >
                  <IconOtherSound fontSize={18} />
                </i>
              );
            }}
          </RequiredAuthFake>

          <Typography.Text className="phonetics-text" variant="regular/18-28">
            {pronounText}
          </Typography.Text>
        </div>
      </div>
      <Separator variant="vertical" backgroundColor={colorsV2.black20} />
      {renderVocabContent()}
    </VocabItemContainer>
  );
});
